const HEADER_NAVIGATION_BREAKPOINT = 1200;
const HEADER_NAVIGATION_MOBILE_PANEL_TRANSITION_MS = 220;
let headerNavigationScrollY = 0;

const lockHeaderNavigationScroll = () => {
  headerNavigationScrollY = window.scrollY;
  document.body.style.position = "fixed";
  document.body.style.top = `-${headerNavigationScrollY}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
  document.body.style.width = "100%";
};

const unlockHeaderNavigationScroll = () => {
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  window.scrollTo(0, headerNavigationScrollY);
};

const initHeaderNavigationDesktop = (root) => {
  const subnav = root.querySelector("[data-desktop-subnav]");
  const primaryPanels = Array.from(root.querySelectorAll("[data-primary-panel]"));
  const secondaryNavLinks = Array.from(root.querySelectorAll(".header-navigation__secondary-link"));
  const secondaryLinks = Array.from(root.querySelectorAll("[data-secondary-trigger]"));
  const secondaryPanels = Array.from(root.querySelectorAll("[data-secondary-panel]"));
  const megaLinkTriggers = Array.from(root.querySelectorAll("[data-mega-link-trigger]"));

  if (!subnav || !primaryPanels.length) {
    return {
      closeAll: () => {},
    };
  }

  const defaultPrimary = root.dataset.defaultPrimary || primaryPanels[0]?.dataset.primaryPanel || "";
  const state = {
    activeSecondary: "",
    secondaryCloseTimer: null,
  };

  const syncPrimary = () => {
    subnav.classList.remove("is-hidden");

    primaryPanels.forEach((panel) => {
      panel.hidden = panel.dataset.primaryPanel !== defaultPrimary;
    });
  };

  const clearSecondaryCloseTimer = () => {
    if (state.secondaryCloseTimer) {
      window.clearTimeout(state.secondaryCloseTimer);
      state.secondaryCloseTimer = null;
    }
  };

  const syncMegaPanel = (panel) => {
    if (!panel) return;

    const activeTertiary = panel.dataset.activeTertiary || panel.dataset.defaultTertiary || "";
    const triggers = Array.from(panel.querySelectorAll("[data-mega-link-trigger]"));
    const productPanels = Array.from(panel.querySelectorAll("[data-mega-products-panel]"));

    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.megaLinkTrigger === activeTertiary;
      trigger.classList.toggle("is-highlighted", isActive);
      trigger.setAttribute("aria-expanded", isActive ? "true" : "false");
    });

    productPanels.forEach((productPanel) => {
      productPanel.hidden = productPanel.dataset.megaProductsPanel !== activeTertiary;
    });
  };

  const activateMegaPanelLink = (tertiaryId) => {
    if (window.innerWidth < HEADER_NAVIGATION_BREAKPOINT || !tertiaryId) return;

    const megaPanel = root.querySelector(`[data-mega-link-trigger="${tertiaryId}"]`)?.closest("[data-secondary-panel]");
    if (!megaPanel) return;

    megaPanel.dataset.activeTertiary = tertiaryId;
    syncMegaPanel(megaPanel);
  };

  const syncSecondary = () => {
    secondaryLinks.forEach((link) => {
      const isActive = link.dataset.secondaryTrigger === state.activeSecondary;
      link.classList.toggle("is-active", isActive);
      link.setAttribute("aria-expanded", isActive ? "true" : "false");
    });

    secondaryPanels.forEach((panel) => {
      const isActive = panel.dataset.secondaryPanel === state.activeSecondary;
      panel.hidden = !isActive;

      if (!isActive) return;

      if (!panel.dataset.activeTertiary && panel.dataset.defaultTertiary) {
        panel.dataset.activeTertiary = panel.dataset.defaultTertiary;
      }

      syncMegaPanel(panel);
    });
  };

  const clearSecondary = () => {
    clearSecondaryCloseTimer();
    state.activeSecondary = "";
    syncSecondary();
  };

  const activateSecondary = (secondaryId) => {
    if (window.innerWidth < HEADER_NAVIGATION_BREAKPOINT || !secondaryId) return;

    clearSecondaryCloseTimer();
    state.activeSecondary = secondaryId;
    syncSecondary();
  };

  const scheduleSecondaryClose = () => {
    if (window.innerWidth < HEADER_NAVIGATION_BREAKPOINT || !state.activeSecondary) return;

    clearSecondaryCloseTimer();
    state.secondaryCloseTimer = window.setTimeout(() => {
      state.secondaryCloseTimer = null;
      clearSecondary();
    }, 120);
  };

  const closeAll = () => {
    syncPrimary();
    clearSecondary();
  };

  secondaryNavLinks.forEach((link) => {
    const secondaryId = link.dataset.secondaryTrigger;
    const handleEnter = () => {
      if (secondaryId) {
        activateSecondary(secondaryId);
      } else {
        clearSecondary();
      }
    };

    link.addEventListener("mouseenter", handleEnter);
    link.addEventListener("focus", handleEnter);
    link.addEventListener("click", (event) => {
      if (!secondaryId) return;

      event.preventDefault();
      activateSecondary(secondaryId);
    });
  });

  megaLinkTriggers.forEach((trigger) => {
    const tertiaryId = trigger.dataset.megaLinkTrigger;
    if (!tertiaryId) return;

    const activate = () => {
      activateMegaPanelLink(tertiaryId);
    };

    trigger.addEventListener("mouseenter", activate);
    trigger.addEventListener("focus", activate);
  });

  primaryPanels.forEach((panelGroup) => {
    panelGroup.addEventListener("mouseenter", () => {
      clearSecondaryCloseTimer();
    });

    panelGroup.addEventListener("mouseleave", (event) => {
      if (window.innerWidth < HEADER_NAVIGATION_BREAKPOINT) return;
      if (event.relatedTarget instanceof Node && panelGroup.contains(event.relatedTarget)) return;

      scheduleSecondaryClose();
    });

    panelGroup.addEventListener("focusin", () => {
      clearSecondaryCloseTimer();
    });

    panelGroup.addEventListener("focusout", () => {
      window.setTimeout(() => {
        if (panelGroup.contains(document.activeElement)) return;

        clearSecondary();
      }, 0);
    });
  });

  document.addEventListener("pointerdown", (event) => {
    if (window.innerWidth < HEADER_NAVIGATION_BREAKPOINT) return;
    if (!root.contains(event.target)) {
      closeAll();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.code.toUpperCase() === "ESCAPE") {
      closeAll();
    }
  });

  window.addEventListener("resize", syncPrimary);

  syncPrimary();
  secondaryPanels.forEach((panel) => syncMegaPanel(panel));

  return { closeAll };
};

const initHeaderNavigationSticky = (root, closeDesktop) => {
  const section = root.closest(".section-header-navigation");
  const topbar = root.querySelector(".header-navigation__topbar");

  if (!section || !topbar) return;

  let isStickyCompact = false;
  let expandedHeight = 0;
  let ticking = false;

  const measureExpandedLayout = () => {
    const wasStickyCompact = root.classList.contains("is-sticky-compact");

    if (wasStickyCompact) {
      root.classList.remove("is-sticky-compact");
      section.classList.remove("is-sticky-active");
    }

    expandedHeight = root.offsetHeight;

    if (wasStickyCompact) {
      root.classList.add("is-sticky-compact");
      section.classList.add("is-sticky-active");
    }
  };

  const getStickyThreshold = () => {
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    return sectionTop + expandedHeight;
  };

  const applyStickyState = (shouldStick) => {
    if (isStickyCompact === shouldStick) return;

    isStickyCompact = shouldStick;
    root.classList.toggle("is-sticky-compact", shouldStick);
    section.classList.toggle("is-sticky-active", shouldStick);
    closeDesktop();
  };

  const syncStickyState = () => {
    ticking = false;

    if (window.innerWidth < HEADER_NAVIGATION_BREAKPOINT) {
      applyStickyState(false);
      section.style.minHeight = "";
      return;
    }

    if (!expandedHeight) {
      measureExpandedLayout();
    }

    section.style.minHeight = `${expandedHeight}px`;
    applyStickyState(window.scrollY > getStickyThreshold());
  };

  const syncStickyMetrics = () => {
    if (window.innerWidth < HEADER_NAVIGATION_BREAKPOINT) {
      expandedHeight = 0;
      section.style.minHeight = "";
      applyStickyState(false);
      return;
    }

    measureExpandedLayout();
    section.style.minHeight = `${expandedHeight}px`;
    syncStickyState();
  };

  const requestSyncStickyState = () => {
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(syncStickyState);
  };

  syncStickyMetrics();

  window.addEventListener("scroll", requestSyncStickyState, { passive: true });
  window.addEventListener("resize", syncStickyMetrics);
};

const initHeaderNavigationMobile = (root, closeDesktop) => {
  const drawer = root.querySelector("[data-mobile-drawer]");
  const overlay = root.querySelector("[data-mobile-drawer-overlay]");
  const toggle = root.querySelector("[data-mobile-drawer-toggle]");
  const closeButton = root.querySelector("[data-mobile-drawer-close]");
  const categoryButtons = Array.from(root.querySelectorAll("[data-mobile-category-toggle]"));
  const searchTriggers = Array.from(root.querySelectorAll("[data-header-navigation-search-trigger]"));
  const cartTrigger = root.querySelector("#cart-icon-bubble");

  if (!drawer || !overlay || !toggle) return;

  document.body.appendChild(overlay);
  document.body.appendChild(drawer);

  let isOpen = false;
  let suppressNextOutsideClick = false;

  const showOverlay = () => {
    overlay.removeAttribute("hidden");
    overlay.classList.add("is-visible");
  };

  const hideOverlay = () => {
    overlay.classList.remove("is-visible");
    overlay.setAttribute("hidden", "");
  };

  const isOutsideDrawerTarget = (target) =>
    target instanceof Node && !drawer.contains(target) && !toggle.contains(target);

  const blockOutsideEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };

  const clearMobilePanelTimers = (panel) => {
    if (!panel) return;

    if (panel._hideTimer) {
      window.clearTimeout(panel._hideTimer);
      panel._hideTimer = null;
    }

    if (panel._resetTimer) {
      window.clearTimeout(panel._resetTimer);
      panel._resetTimer = null;
    }
  };

  const hideMobileCategoryPanel = (panel, immediate = false) => {
    if (!panel) return;

    clearMobilePanelTimers(panel);

    if (immediate || panel.hidden) {
      panel.classList.remove("is-open");
      panel.hidden = true;
      panel.style.height = "";
      return;
    }

    panel.style.height = `${panel.scrollHeight}px`;

    window.requestAnimationFrame(() => {
      panel.classList.remove("is-open");
      panel.style.height = "0px";
    });

    panel._hideTimer = window.setTimeout(() => {
      panel.hidden = true;
      panel.style.height = "";
      panel._hideTimer = null;
    }, HEADER_NAVIGATION_MOBILE_PANEL_TRANSITION_MS);
  };

  const showMobileCategoryPanel = (panel) => {
    if (!panel) return;

    clearMobilePanelTimers(panel);
    panel.hidden = false;
    panel.style.height = "0px";
    panel.classList.add("is-open");

    window.requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
    });

    panel._resetTimer = window.setTimeout(() => {
      if (panel.classList.contains("is-open")) {
        panel.style.height = "auto";
      }
      panel._resetTimer = null;
    }, HEADER_NAVIGATION_MOBILE_PANEL_TRANSITION_MS);
  };

  const collapseCategories = ({ immediate = false, exceptPanelId = "" } = {}) => {
    categoryButtons.forEach((button) => {
      const category = button.closest(".header-navigation__mobile-category");
      const panelId = button.dataset.mobileCategoryToggle;
      const panel = drawer.querySelector(`#${panelId}`);

      if (exceptPanelId && panelId === exceptPanelId) return;

      button.setAttribute("aria-expanded", "false");
      category?.classList.remove("is-open");
      hideMobileCategoryPanel(panel, immediate);
    });
  };

  const closeDrawer = (restoreFocus = true) => {
    if (!isOpen) return;

    isOpen = false;
    hideOverlay();
    drawer.querySelectorAll("localization-form").forEach((localizationForm) => {
      if (typeof localizationForm.hidePanel === "function") {
        localizationForm.hidePanel();
      }
    });
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    toggle.setAttribute("aria-expanded", "false");
    collapseCategories({ immediate: true });
    unlockHeaderNavigationScroll();

    if (restoreFocus) {
      toggle.focus();
    }
  };

  const openDrawer = () => {
    if (window.innerWidth >= HEADER_NAVIGATION_BREAKPOINT || isOpen) return;

    isOpen = true;
    closeDesktop();
    lockHeaderNavigationScroll();
    showOverlay();
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    toggle.setAttribute("aria-expanded", "true");
    setTimeout(() => closeButton?.focus(), 20);
  };

  const openSearchModal = (trigger) => {
    const searchSummary = root.querySelector(".header-navigation__search-modal-host .header__icon--search");

    if (!searchSummary) {
      const fallbackUrl = trigger?.getAttribute("href");
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    searchSummary.click();
  };

  toggle.addEventListener("click", () => {
    if (isOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  });

  closeButton?.addEventListener("click", () => closeDrawer());
  overlay.addEventListener("click", () => closeDrawer(false));

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (!isOpen || window.innerWidth >= HEADER_NAVIGATION_BREAKPOINT) return;
      if (!isOutsideDrawerTarget(event.target)) return;

      suppressNextOutsideClick = true;
      blockOutsideEvent(event);
      closeDrawer(false);
    },
    true
  );

  document.addEventListener(
    "click",
    (event) => {
      const shouldSuppressClick = suppressNextOutsideClick && isOutsideDrawerTarget(event.target);

      if (!isOpen && !shouldSuppressClick) return;
      if (!isOutsideDrawerTarget(event.target)) return;

      suppressNextOutsideClick = false;
      blockOutsideEvent(event);

      if (isOpen) {
        closeDrawer(false);
      }
    },
    true
  );

  categoryButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const category = button.closest(".header-navigation__mobile-category");
      const panelId = button.dataset.mobileCategoryToggle;
      const panel = drawer.querySelector(`#${panelId}`);
      const willOpen = button.getAttribute("aria-expanded") !== "true";

      collapseCategories({ exceptPanelId: willOpen ? panelId : "" });

      if (willOpen && panel) {
        button.setAttribute("aria-expanded", "true");
        category?.classList.add("is-open");
        showMobileCategoryPanel(panel);
      }
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.code.toUpperCase() === "ESCAPE" && isOpen) {
      closeDrawer();
    }
  });

  cartTrigger?.addEventListener("click", () => {
    if (isOpen) {
      closeDrawer(false);
    }
  });

  searchTriggers.forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      closeDesktop();

      const shouldWaitForDrawer = isOpen;
      if (isOpen) {
        closeDrawer(false);
      }

      window.setTimeout(() => {
        openSearchModal(trigger);
      }, shouldWaitForDrawer ? 300 : 0);
    });
  });

  document.addEventListener("country-change-modal:open", () => {
    if (isOpen) {
      closeDrawer(false);
    }
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth >= HEADER_NAVIGATION_BREAKPOINT) {
      closeDrawer(false);
    }
  });
};

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-header-navigation]").forEach((headerRoot) => {
    const desktop = initHeaderNavigationDesktop(headerRoot);
    initHeaderNavigationSticky(headerRoot, desktop.closeAll);
    initHeaderNavigationMobile(headerRoot, desktop.closeAll);

    headerRoot.querySelectorAll(".header-navigation__icon-button--cart, .header-navigation__icon-button--search").forEach((link) => {
      link.addEventListener("click", () => {
        desktop.closeAll();
      });
    });
  });
});
